import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(() =>
    localStorage.getItem('accessToken')
  );
  const navigate = useNavigate();

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!token && !refreshToken) {
        setLoading(false);
        return;
      }

      try {
        // Try to get current user
        if (token) {
          const response = await api.get('/users/me');
          setUser(response.data.user);
        } else if (refreshToken) {
          // Try to refresh the token
          await refresh();
        }
      } catch (error) {
        console.error('Auth init error:', error);
        // Clear invalid tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setAccessToken(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Update axios headers when token changes
  useEffect(() => {
    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [accessToken]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { user, accessToken, refreshToken } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setAccessToken(accessToken);
    setUser(user);

    return user;
  }, []);

  const register = useCallback(async (email, password, role = 'ATHLETE') => {
    // Get guest sport data from localStorage (saved during guest browsing)
    const guestSportsKey = 'prism_guest_sports';
    const guestSportsRaw = localStorage.getItem(guestSportsKey);
    let guestSportData = null;

    if (guestSportsRaw) {
      try {
        const parsed = JSON.parse(guestSportsRaw);
        guestSportData = {
          primarySport: parsed.primary,
          secondarySports: parsed.secondary || [],
        };
      } catch {
        // Invalid data, ignore
      }
    }

    const payload = {
      email,
      password,
      role,
      ...(guestSportData && { guestSportData }),
    };

    const response = await api.post('/auth/register', payload);
    const { user, accessToken, refreshToken } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // Clear guest data after successful registration
    if (guestSportData) {
      localStorage.removeItem(guestSportsKey);
    }

    setAccessToken(accessToken);
    setUser(user);

    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setAccessToken(null);
      setUser(null);
      navigate('/');
    }
  }, [navigate]);

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await api.post('/auth/refresh', { refreshToken });
    const { accessToken: newAccessToken, user } = response.data;

    localStorage.setItem('accessToken', newAccessToken);
    setAccessToken(newAccessToken);
    setUser(user);

    return newAccessToken;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const forgotPassword = useCallback(async (email) => {
    await api.post('/auth/forgot-password', { email });
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    await api.post('/auth/reset-password', { token, password });
  }, []);

  const verifyEmail = useCallback(async (token) => {
    await api.post('/auth/verify-email', { token });
    // Update user's email verified status
    setUser((prev) => (prev ? { ...prev, emailVerified: true } : null));
  }, []);

  const resendVerification = useCallback(async () => {
    await api.post('/auth/resend-verification');
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    accessToken,
    login,
    register,
    logout,
    refresh,
    updateUser,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
